// hooks/useMediaCapture.ts
'use client';

import { useRef, useState, useCallback } from 'react';

export interface CapturedMedia {
    id: string;
    blob: Blob;
    localUrl: string;
    type: 'image' | 'video';
    mimeType: string;
}

export interface CapturedMediaState {
    screenshots: CapturedMedia[];
    recording: CapturedMedia | null;
}

export function useMediaCapture() {
    const [media, setMedia] = useState<CapturedMediaState>({
        screenshots: [],
        recording: null,
    });
    const [isRecording, setIsRecording] = useState(false);
    // isCapturing is true ONLY during the getDisplayMedia permission prompt.
    // It goes false as soon as permission is granted or denied.
    // It is intentionally separate from isRecording so the modal stays
    // visible while recording is active — only hides during the prompt flash.
    const [isCapturing, setIsCapturing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);

    // ── Helpers ────────────────────────────────────────────────────────────

    const addScreenshot = useCallback((captured: CapturedMedia) => {
        setMedia((prev) => ({
            ...prev,
            screenshots: [...prev.screenshots, captured],
        }));
    }, []);

    const removeScreenshot = useCallback((id: string) => {
        setMedia((prev) => {
            const target = prev.screenshots.find((s) => s.id === id);
            if (target) URL.revokeObjectURL(target.localUrl);
            return {
                ...prev,
                screenshots: prev.screenshots.filter((s) => s.id !== id),
            };
        });
    }, []);

    const resetRecording = useCallback(() => {
        setMedia((prev) => {
            if (prev.recording?.localUrl) URL.revokeObjectURL(prev.recording.localUrl);
            return { ...prev, recording: null };
        });
    }, []);

    // resetAll clears everything — only call on widget close, not during capture
    const resetAll = useCallback(() => {
        setMedia((prev) => {
            prev.screenshots.forEach((s) => URL.revokeObjectURL(s.localUrl));
            if (prev.recording?.localUrl) URL.revokeObjectURL(prev.recording.localUrl);
            return { screenshots: [], recording: null };
        });
        setError(null);
        setIsRecording(false);
        setIsCapturing(false);
    }, []);

    // ── Screenshot ─────────────────────────────────────────────────────────

    const captureScreenshot = useCallback(async (): Promise<CapturedMedia | null> => {
        setError(null);
        // isCapturing: true only during the browser permission prompt.
        // Widget hides the modal for this brief moment so it doesn't
        // appear in the screenshot.
        setIsCapturing(true);
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });

            const video = document.createElement('video');
            video.srcObject = stream;
            await new Promise<void>((resolve) => {
                video.onloadedmetadata = () => resolve();
                void video.play();
            });
            await new Promise((r) => setTimeout(r, 100));

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d')!.drawImage(video, 0, 0);
            stream.getTracks().forEach((t) => t.stop());

            const blob = await new Promise<Blob>((res) =>
                canvas.toBlob((b) => res(b!), 'image/png')
            );

            return {
                id: crypto.randomUUID(),
                blob,
                localUrl: URL.createObjectURL(blob),
                type: 'image',
                mimeType: 'image/png',
            };
        } catch (err: unknown) {
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                setError('Screenshot failed. Please try again.');
            }
            return null;
        } finally {
            // Always restore isCapturing so modal reappears
            setIsCapturing(false);
        }
    }, []);

    // ── Recording ──────────────────────────────────────────────────────────

    const startRecording = useCallback(async () => {
        setError(null);
        // Only set isCapturing=true during the permission prompt (before stream is granted).
        // Once we have the stream, set it back to false so the modal stays visible
        // while recording proceeds — this is the key fix for screenshot loss.
        setIsCapturing(true);
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: true,
            });
            // Permission granted — restore modal immediately
            setIsCapturing(false);

            streamRef.current = stream;
            chunksRef.current = [];

            const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
                ? 'video/webm;codecs=vp9'
                : 'video/webm';

            const recorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                // Use functional update so we never overwrite the screenshots array
                setMedia((prev) => ({
                    ...prev,
                    recording: {
                        id: crypto.randomUUID(),
                        blob,
                        localUrl: URL.createObjectURL(blob),
                        type: 'video',
                        mimeType: 'video/webm',
                    },
                }));
                setIsRecording(false);
                // Stop tracks here, after the recorder has flushed all data.
                // Stopping tracks before onstop can cause the final dataavailable
                // chunk to be lost, leaving the blob empty.
                streamRef.current?.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            };

            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.onended = () => {
                    if (recorder.state === 'recording') recorder.stop();
                    setIsRecording(false);
                };
            }

            recorder.start(1000);
            setIsRecording(true);
        } catch (err: unknown) {
            setIsCapturing(false);
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                setError('Recording failed to start.');
            }
        }
    }, []);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state === 'recording') {
            recorder.stop();
        }
    }, []);

    const hasAnyMedia = media.screenshots.length > 0 || Boolean(media.recording);

    return {
        captureScreenshot,
        startRecording,
        stopRecording,
        isRecording,
        isCapturing,
        media,
        hasAnyMedia,
        error,
        addScreenshot,
        removeScreenshot,
        resetRecording,
        resetAll,
    };
}
